import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import SharedHeader from '../components/layout/SharedHeader';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount / 100);
};

const PRODUCT_ORDER = [
  {
    id: "Risk Advisor",
    order: 1,
    bundleGroup: "core"
  },
  {
    id: "Weather Advisor",
    order: 2,
    bundleGroup: "core"
  },
  {
    id: "NOTAM Advisor",
    order: 3,
    bundleGroup: "core"
  },{
    id: "Safety Advisor",
    order: 4,
    bundleGroup: "operations"
  },{
    id: "Airfield Advisor",
    order: 5,
    bundleGroup: "operations"
  },
  {
    id: "Maintenance Advisor",
    order: 7,
    bundleGroup: "operations"
  },
  {
    id: "SOP Advisor",
    order: 8,
    bundleGroup: "policy"
  },
  {
    id: "FAA Advisor",
    order: 7,
    bundleGroup: "policy"
  },
  {
    id: "Pilot Examiner Advisor",
    order: 6,
    bundleGroup: "professional"
  }
];

// Add bundle definitions
const BUNDLES = {
  core: {
    name: "Core Aviation Bundle",
    discount: 0.15, // 15% discount
    products: ["Risk Advisor", "Weather Advisor", "NOTAM Advisor"]
  },
  operations: {
    name: "Operations Bundle",
    discount: 0.20, // 20% discount
    products: ["Airfield Advisor", "Maintenance Advisor", "Safety Advisor"]
  },
  policy: {
    name: "Policy Bundle",
    discount: 0.20, // 20% discount
    products: ["FAA Advisor", "SOP Advisor"]
  },
  professional: {
    name: "Professional Bundle",
    discount: 0.20, // 20% discount
    products: ["Risk Advisor", "Weather Advisor", "NOTAM Advisor" , "Airfield Advisor", "Maintenance Advisor", "Safety Advisor", "FAA Advisor", "SOP Advisor"]
  }
};

// Initial state with proper types
const initialState = {
  prices: { plans: [], subscriptions: [] },
  subscriptions: [], // Ensure this is always an array
  loading: true,
  error: null,
  processing: false,
  billingCycle: 'plans'
};

// Moved API client creation to a separate utility
const createAPIClient = (user) => {
  const baseUrl = `${process.env.REACT_APP_API_URL}/api`;

  const fetchWithAuth = async (endpoint, options = {}) => {
    try {
      const token = await user.getIdToken(true);
      const url = `${baseUrl}${endpoint}`;
      
      const config = {
        method: options.method || 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          ...(options.headers || {})
        },
        credentials: 'include'
      };

      // Only add Content-Type and body for POST/PUT requests
      if (options.method && options.method !== 'GET') {
        config.headers['Content-Type'] = 'application/json';
        if (options.body) {
          config.body = JSON.stringify(options.body);
        }
      }

      console.log('Making request to:', url, {
        method: config.method,
        ...(config.body && { body: JSON.parse(config.body) })
      });

      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: `HTTP error! status: ${response.status}`
        }));
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  };

  return {
    getPrices: () => fetchWithAuth('/stripe/prices'),
    getProducts: () => fetchWithAuth('/stripe/products'),
    getSubscriptions: () => fetchWithAuth('/stripe/subscriptions'),
    createCheckoutSession: (priceId) => 
      fetchWithAuth('/stripe/create-checkout-session', {
        method: 'POST',
        body: { priceId }
      }),
    createPortalSession: (customerId, return_url) =>
      fetchWithAuth('/stripe/create-portal-session', {
        method: 'POST',
        body: { 
          customerId,
          return_url
        }
      })
  };
};

const SubscriptionManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hasActiveSubscription } = useSubscription();
  const [state, setState] = useState(initialState);
  const [processingId, setProcessingId] = useState(null); 
  const [products, setProducts] = useState({});
  const [dataFetched, setDataFetched] = useState(false);
  const [setIsLoading] = useState(false);

  const environment = process.env.NODE_ENV || 'development';

  // 1. API Client
  const apiClient = useCallback(() => user && createAPIClient(user), [user]);

  // 2. Handle Subscribe
  const handleSubscribe = useCallback(async (priceId) => {
    if (!user) return;

    setProcessingId(priceId);
    try {
      const client = apiClient();
      const response = await client.createCheckoutSession(priceId);
      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      setState(prev => ({ ...prev, error: error.message }));
    } finally {
      setProcessingId(null);
    }
  }, [user, apiClient]);

  // 3. Handle Manage Subscription
  const handleManageSubscription = useCallback(async (subscriptionId) => {
    if (!user || state.processing) return;

    setState(prev => ({ ...prev, processing: true, error: null }));
    try {
      // Get Stripe customer ID from Firestore
      const pilotDoc = await getDoc(doc(db, 'pilot', user.uid));
      const stripeCustomerId = pilotDoc.data()?.stripeCustomerId;
      
      if (!stripeCustomerId) {
        throw new Error('No Stripe customer ID found in pilot document');
      }
      
      const client = apiClient();
      const response = await client.createPortalSession({
        customerId: stripeCustomerId,
        return_url: `${window.location.origin}/subscription`
      });
      
      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No redirect URL received from portal session creation');
      }
    } catch (error) {
      console.error('Portal session creation error:', error);
      setState(prev => ({ 
        ...prev, 
        error: `Failed to access billing portal: ${error.message}` 
      }));
    } finally {
      setState(prev => ({ ...prev, processing: false }));
    }
  }, [user, apiClient, state.processing]);

  // 4. isSubscribed helper
  const isSubscribed = useCallback((priceId) => {
    return state.subscriptions.some(sub => 
      sub.status === 'active' && 
      (sub.plan.id === priceId || sub.plan.id === priceId?.id)
    );
  }, [state.subscriptions]);

  // 5. Fetch Products
  const fetchProducts = useCallback(async () => {
    if (!user) return;
    
    try {
      const client = apiClient();
      const response = await client.getProducts();
      const productsArray = await response.json();
      
      const productMap = {};
      if (Array.isArray(productsArray)) {
        productsArray.forEach(product => {
          productMap[product.id] = product.name; // Map product ID to product name
        });
      }
      
      setProducts(productMap);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts({});
    }
  }, [user, apiClient]);

  // 6. Fetch Subscription Data
  const fetchSubscriptionData = useCallback(async () => {
    if (!user) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const client = apiClient();
      const [pricesResponse, subscriptionsResponse] = await Promise.all([
        client.getPrices(),
        client.getSubscriptions()
      ]);

      const [pricesData, subscriptionsData] = await Promise.all([
        pricesResponse.json(),
        subscriptionsResponse.json()
      ]);

      const normalizedSubscriptions = Array.isArray(subscriptionsData) 
        ? subscriptionsData 
        : subscriptionsData?.subscriptions || [];

      setState(prev => ({
        ...prev,
        prices: pricesData.prices || { plans: [], subscriptions: [] },
        subscriptions: normalizedSubscriptions,
        loading: false,
        error: null
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
      setState(prev => ({
        ...prev,
        error: error.message,
        loading: false,
        subscriptions: []
      }));
    }
  }, [user, apiClient]);

  // 7. Process Prices
  const processPrices = useCallback((prices) => {
    const orderMap = new Map(PRODUCT_ORDER.map(p => [p.id, p]));
    
    return prices.sort((a, b) => {
      const orderA = orderMap.get(a.nickname)?.order || 999;
      const orderB = orderMap.get(b.nickname)?.order || 999;
      return orderA - orderB;
    }).map(price => {
      const orderInfo = orderMap.get(price.nickname);
      const bundle = orderInfo ? BUNDLES[orderInfo.bundleGroup] : null;
      
      const bundlePrice = bundle ? {
        originalPrice: price.unit_amount,
        discountedPrice: price.unit_amount * (1 - bundle.discount),
        bundleName: bundle.name,
        bundleDiscount: bundle.discount * 100
      } : null;

      return {
        ...price,
        bundleInfo: bundlePrice
      };
    });
  }, []);

  // 8. Render functions after all other functions are defined
  const renderPriceCards = useCallback(() => {
    const processedPrices = processPrices(state.prices.plans || []);
    console.log('Processed Prices with Bundles:', processedPrices);
    console.log('Raw Prices:', state.prices.plans);
    
    return (
      <>
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Bundles</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {Object.values(BUNDLES).map(bundle => (
              <div key={bundle.name} className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900">{bundle.name}</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Save {bundle.discount * 100}% when you subscribe to these products together
                </p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {processedPrices.map(price => (
            <PricingCard
              key={price.id?.id || price.id}
              price={price}
              onSubscribe={handleSubscribe}
              processingId={processingId}
              isSubscribed={isSubscribed(price.id)}
            />
          ))}
        </div>
      </>
    );
  }, [state.prices.plans, processingId, handleSubscribe, isSubscribed, processPrices]);

  const renderActiveSubscriptions = useCallback(() => {
    const formatDate = (unixTimestamp) => {
      return new Date(unixTimestamp * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formatAmount = (amount, interval) => {
      const formattedAmount = formatCurrency(amount);
      return `${formattedAmount}/${interval === 'year' ? 'year' : 'month'}`;
    };
    
    return (
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Active Subscriptions</h2>
        <div className="space-y-4">
          {state.subscriptions.map(sub => {
            const productId = sub.plan.product || sub.plan.product_id;
            const productName = products[productId] || 'Unknown Product';

            return (
              <div key={sub.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold">
                      {`${productName} Subscription (${formatAmount(sub.plan.amount, sub.plan.interval)})`}
                    </h3>
                    <div className="mt-2 space-y-1">
                      <p className="text-gray-500">
                        Renewal Date: {formatDate(sub.current_period_end)}
                      </p>
                      <p className="text-gray-500">
                        {sub.cancel_at_period_end 
                          ? '🔴 Will cancel at end of period' 
                          : '🟢 Will auto-renew'}
                      </p>
                    </div>
                    {sub.plan.nickname && (
                      <p className="text-sm text-gray-600 mt-2">
                        {formatAmount(sub.plan.amount, sub.plan.interval)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium 
                      ${sub.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'}`}
                    >
                      {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                    </span>
                    <button
                      onClick={handleManageSubscription}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      disabled={state.processing}
                    >
                      {state.processing ? 'Processing...' : 'Manage'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [state.subscriptions, products, handleManageSubscription, state.processing]);

  // Fetch all data at once
  const fetchAllData = useCallback(async () => {
    if (!user || dataFetched) return;
    
    setIsLoading(true);
    try {
      const client = apiClient();
      
      const [pricesResponse, subscriptionsResponse, productsResponse] = await Promise.all([
        client.getPrices(),
        client.getSubscriptions(),
        client.getProducts()
      ]);

      const [pricesData, subscriptionsData, productsArray] = await Promise.all([
        pricesResponse.json(),
        subscriptionsResponse.json(),
        productsResponse.json()
      ]);

      // Create products map
      const productMap = {};
      if (Array.isArray(productsArray)) {
        productsArray.forEach(product => {
          productMap[product.id] = product.name;
        });
      }
      setProducts(productMap);

      console.log('Products Map:', productMap); // Debug log
      console.log('Subscriptions:', subscriptionsData); // Debug log

      setState(prev => ({
        ...prev,
        prices: { 
          plans: Array.isArray(pricesData) ? pricesData : [],
          subscriptions: []
        },
        subscriptions: Array.isArray(subscriptionsData) 
          ? subscriptionsData 
          : subscriptionsData?.subscriptions || [],
        loading: false,
        error: null
      }));

    } catch (error) {
      console.error('Error fetching data:', error);
      setState(prev => ({
        ...prev,
        error: error.message,
        loading: false,
        subscriptions: []
      }));
    } finally {
      setIsLoading(false);
      setDataFetched(true);
    }
  }, [user, apiClient, dataFetched]);

  // Single effect to fetch all data
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  /*useEffect(() => {
    // Temporarily disable redirect
    // if (hasActiveSubscription) {
    //   navigate('/chat');
    // }
  }, [hasActiveSubscription, navigate]);*/

 const PricingCard = ({ price, onSubscribe, processingId, isSubscribed }) => {
  const hasBundle = price.bundleInfo !== null;
  const productId = price.product || price.product_id;
  const productName = products[productId] || 'Unknown Product';
  
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-6">
        {hasBundle && (
          <div className="mb-4 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm inline-block">
            Part of {price.bundleInfo.bundleName}
          </div>
        )}
        <h3 className="text-xl font-semibold text-gray-900">
          {state.subscriptions.map(sub => {
            const productId = sub.plan.product || sub.plan.product_id;
            const productName = products[productId] || 'Unknown Product';
            return productName;
          })}
        </h3>
        <p className="mt-2 text-gray-500">
          {price.description || 'Enhance your aviation experience with our professional tools.'}
        </p>
        <div className="mt-4">
          {hasBundle ? (
            <div className="space-y-1">
              <p className="text-3xl font-bold text-gray-900">
                {formatCurrency(price.bundleInfo.discountedPrice)}
                <span className="text-lg font-normal text-gray-500">/month</span>
              </p>
              <p className="text-sm text-gray-500 line-through">
                {formatCurrency(price.bundleInfo.originalPrice)}/month
              </p>
              <p className="text-sm text-green-600 font-medium">
                Save {price.bundleInfo.bundleDiscount}% with bundle
              </p>
            </div>
          ) : (
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(price.unit_amount)}
              <span className="text-lg font-normal text-gray-500">/month</span>
            </p>
          )}
        </div>
        <button
          onClick={() => onSubscribe(price.id?.id || price.id)}
          disabled={processingId === (price.id?.id || price.id) || isSubscribed}
          className={`mt-8 w-full py-3 px-4 rounded-md text-white text-sm font-semibold
            ${isSubscribed 
              ? 'bg-green-500 cursor-not-allowed' 
              : processingId === (price.id?.id || price.id)
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
        >
          {isSubscribed 
            ? 'Currently Subscribed' 
            : processingId === (price.id?.id || price.id)
              ? 'Processing...' 
              : hasBundle ? 'Subscribe to Bundle' : 'Subscribe Now'}
        </button>
      </div>
    </div>
  );
};

  if (state.loading) {
    return (
      <>
        <SharedHeader environment={environment} />
        <div className="min-h-screen bg-neutral-white flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        </div>
      </>
    );
  }

  return (
    <>
      <SharedHeader environment={environment} />
      <div className="min-h-screen bg-neutral-white py-8 px-4 sm:px-6 lg:px-8">
        {state.error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {state.error}
              {environment === 'development' && (
                <div className="mt-2 text-sm">
                  <strong>Development Note:</strong> Check the console for detailed error information.
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
            <div className="mt-6 flex justify-center space-x-4">
              <button
                onClick={() => setState(prev => ({ ...prev, billingCycle: 'plans' }))}
                className={`px-4 py-2 rounded-md ${
                  state.billingCycle === 'plans' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Available Plans
              </button>
              <button
                onClick={() => setState(prev => ({ ...prev, billingCycle: 'subscriptions' }))}
                className={`px-4 py-2 rounded-md ${
                  state.billingCycle === 'subscriptions'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                My Subscriptions
              </button>
            </div>
          </div>

          {console.log('Rendering with billingCycle:', state.billingCycle)}
          {state.billingCycle === 'plans' ? renderPriceCards() : renderActiveSubscriptions()}

        </div>
      </div>
    </>
  );
};

export default SubscriptionManagement;